import React, { useState, useEffect, useCallback } from "react";
import { getStoredTheme, setStoredTheme } from "../../../utils/theme";
import {
  getStoredPlayerName,
  setStoredPlayerName,
} from "../../../utils/storage";
import { ProfileDialog } from "../ProfileDialog";
import { NamePrompt } from "../NamePrompt";
import "./GlobalHeader.css";

interface GlobalHeaderProps {
  onThemeChange?: (theme: "dark" | "light") => void;
}

export const GlobalHeader: React.FC<GlobalHeaderProps> = ({
  onThemeChange,
}) => {
  const [theme, setTheme] = useState<"dark" | "light">(() => getStoredTheme());
  const [playerName, setPlayerName] = useState<string | null>(() =>
    getStoredPlayerName(),
  );
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showNamePrompt, setShowNamePrompt] = useState(false);

  // Listen for storage changes (when name is set from other components)
  useEffect(() => {
    const checkName = () => {
      const storedName = getStoredPlayerName();
      if (storedName !== playerName) {
        setPlayerName(storedName);
      }
    };

    // Check periodically for name changes
    const interval = setInterval(checkName, 500);

    // Also listen for storage events (cross-tab)
    window.addEventListener("storage", checkName);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkName);
    };
  }, [playerName]);

  const handleThemeSwitch = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    setStoredTheme(next);
    onThemeChange?.(next);
    // Update body class for global theme
    document.body.classList.remove("theme-dark", "theme-light");
    document.body.classList.add(`theme-${next}`);
  };

  const handleProfileClick = () => {
    if (playerName) {
      setShowProfileDialog(true);
    } else {
      setShowNamePrompt(true);
    }
  };

  const handleProfileSave = (newName: string) => {
    setPlayerName(newName);
    setStoredPlayerName(newName);
    setShowProfileDialog(false);
  };

  const handleNameSubmit = (name: string) => {
    setPlayerName(name);
    setStoredPlayerName(name);
    setShowNamePrompt(false);
  };

  return (
    <>
      <div className="global-header">
        <button
          className="global-header__profile-btn"
          onClick={handleProfileClick}
          type="button"
          title="Profil bearbeiten"
        >
          <span
            className="global-header__profile-icon"
            role="img"
            aria-label="profile"
          >
            👤
          </span>
          <span className="global-header__profile-name">
            {playerName || "Profil"}
          </span>
        </button>
        <button
          onClick={handleThemeSwitch}
          title={theme === "dark" ? "Heller Modus" : "Dunkler Modus"}
          className="global-header__theme-btn"
        >
          {theme === "dark" ? "☀️" : "🌙"}
        </button>
      </div>
      {showNamePrompt && (
        <NamePrompt
          onSubmit={handleNameSubmit}
          onClose={() => setShowNamePrompt(false)}
        />
      )}
      {showProfileDialog && playerName && (
        <ProfileDialog
          currentName={playerName}
          onSave={handleProfileSave}
          onClose={() => setShowProfileDialog(false)}
        />
      )}
    </>
  );
};
